<template>
  <el-dialog
    append-to-body
    :modal="$attrs.modal"
    :title="dialogTitle"
    :close-on-click-modal="false"
    :visible="visible"
    width="600px"
    class="page-dialog center-dialog"
    destroy-on-close
    @close="$emit('update:visible', false)"
    @closed="handleClosed"
  >
    <el-form ref="formRef" :model="form" :rules="formRules" label-width="150px" size="small">
      <el-form-item v-if="!isEdit" label="会员账号" prop="username">
        <div class="username-row">
          <el-input
            v-model="form.username"
            placeholder="请输入会员账号"
            maxlength="20"
            show-word-limit
            clearable
          ></el-input>
          <el-button
            size="small"
            type="primary"
            :loading="checkLoading"
            style="margin-left: 10px"
            @click="handleCheckUser"
            >查询</el-button
          >
        </div>
      </el-form-item>
      <el-form-item v-else label="会员账号">
        <el-input v-model="form.username" disabled></el-input>
      </el-form-item>
      <div v-if="!isEdit && isUserExists && canBindNumber === 0" class="error-message">绑卡数已达上限</div>

      <component
        :is="countryFieldsComponent"
        v-if="showAddFormItem"
        ref="countryFieldsRef"
        :key="siteCountry"
        :form="form"
      />
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleCancel">取消</el-button>
      <el-button size="small" type="primary" :disabled="!showAddFormItem" @click="handleSubmit">确认</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { addPaymentMethod, checkUserExists, editPaymentMethod } from '@/api/member/account'
import {
  getBankAccountSiteCountry,
  resolveBankAccountFormFieldsComponent,
} from './bankAccountForm/bankAccountFormCountry'

const PIX_TYPE_BANK = 'BANK'
const PIX_TYPE_IMPS = 'IMPS'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function emptyForm() {
  return {
    id: '',
    username: '',
    pix_type: '',
    card_type: '',
    card_no: '',
    extra1: '',
    real_name: '',
    mobile: '',
    email: '',
  }
}

export default {
  name: 'BankAccountFormDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isUserExists: false,
      userInfo: {},
      form: emptyForm(),
      checkLoading: false,
    }
  },
  computed: {
    siteCountry() {
      return getBankAccountSiteCountry()
    },
    countryFieldsComponent() {
      return resolveBankAccountFormFieldsComponent(this.siteCountry)
    },
    canBindNumber() {
      return this.userInfo?.can_bind_number || 0
    },
    showAddFormItem() {
      return (this.isUserExists && this.canBindNumber) || this.isEdit
    },
    dialogTitle() {
      return this.isEdit ? '编辑账户' : '新增账户'
    },
    formRules() {
      const usernameRules = this.isEdit
        ? []
        : [{ required: true, message: '请输入会员账号并点击查询', trigger: 'blur' }]
      const base = {
        username: usernameRules,
      }
      const c = this.siteCountry
      if (c === 'ng') {
        return {
          ...base,
          card_type: [{ required: true, message: '请选择银行名称', trigger: 'change' }],
          card_no: [{ required: true, message: '请输入卡号', trigger: 'blur' }],
        }
      }
      if (c === 'br') {
        return {
          ...base,
          pix_type: [{ required: true, message: '请选择 PIX 类型', trigger: 'change' }],
          card_no: [{ required: true, validator: this.validateBrCardNo, trigger: ['blur', 'change'] }],
        }
      }
      if (c === 'in') {
        return {
          ...base,
          card_type: [{ required: true, message: '请输入 IFSC', trigger: 'blur' }],
          card_no: [{ required: true, message: '请输入银行卡号', trigger: 'blur' }],
          real_name: [{ required: true, message: '请输入开户姓名', trigger: 'blur' }],
        }
      }
      return base
    },
  },
  watch: {
    formData: {
      handler(val) {
        if (val && this.visible) {
          this.applyFormPayload(val)
        }
      },
      immediate: true,
      deep: true,
    },
    visible(val) {
      if (val && this.formData) {
        this.applyFormPayload(this.formData)
        if (this.isEdit) {
          this.handleCheckUser()
          this.$nextTick(() => this.loadCountryFieldsIfNeeded())
        }
      } else if (!val) {
        this.isUserExists = false
        this.userInfo = {}
      }
    },
  },
  methods: {
    applyFormPayload(val) {
      const v = val || {}
      this.form = {
        ...emptyForm(),
        id: v.id || '',
        username: v.username || '',
        pix_type: v.pix_type != null && v.pix_type !== '' ? v.pix_type : '',
        card_type: v.card_type || '',
        card_no: v.card_no == null ? '' : String(v.card_no),
        extra1: v.extra1 == null ? '' : String(v.extra1),
        real_name: v.real_name == null ? '' : String(v.real_name),
        mobile: v.mobile == null ? '' : String(v.mobile),
        email: v.email == null ? '' : String(v.email),
      }
      this.applyCountryDefaults()
    },
    applyCountryDefaults() {
      const c = this.siteCountry
      if (c === 'ng') {
        this.form.pix_type = PIX_TYPE_BANK
      } else if (c === 'br') {
        const allowed = ['CPF', 'PHONE', 'CNPJ', 'EMAIL']
        if (!allowed.includes(this.form.pix_type)) {
          this.form.pix_type = 'CPF'
        }
        this.form.card_type = 'PIX'
      } else if (c === 'in') {
        this.form.pix_type = PIX_TYPE_IMPS
      }
    },
    validateBrCardNo(rule, value, callback) {
      const t = this.form.pix_type
      const raw = String(value || '').trim()
      if (!raw) {
        callback(new Error('请输入 PIX 账号'))
        return
      }
      const digits = raw.replace(/\D/g, '')
      if (t === 'CPF') {
        if (digits.length !== 11) callback(new Error('CPF 须为 11 位数字'))
        else callback()
        return
      }
      if (t === 'CNPJ') {
        if (digits.length !== 14) callback(new Error('CNPJ 须为 14 位数字'))
        else callback()
        return
      }
      if (t === 'PHONE') {
        if (digits.length < 10 || digits.length > 11) callback(new Error('手机号须为 10～11 位数字'))
        else callback()
        return
      }
      if (t === 'EMAIL') {
        if (!EMAIL_RE.test(raw)) callback(new Error('请输入有效邮箱'))
        else callback()
        return
      }
      callback()
    },
    loadCountryFieldsIfNeeded() {
      if (this.siteCountry !== 'ng') return
      this.$refs.countryFieldsRef?.loadBankOptions?.()
    },
    async handleCheckUser() {
      const username = this.form.username || ''
      if (!username) {
        this.$message.warning('请输入会员账号')
        return
      }
      if (username.length > 20) {
        this.$message.warning('会员账号最多20位')
        return
      }
      this.checkLoading = true
      try {
        const res = await checkUserExists({ username })
        if (res.code === 0 && res.data?.uid) {
          this.isUserExists = true
          this.userInfo = res.data
          this.form.username = username
          await this.$nextTick()
          this.loadCountryFieldsIfNeeded()
        } else {
          this.$message.warning('用户不存在')
          this.isUserExists = false
        }
      } catch (e) {
        console.error(e)
        this.$message.error('查询失败')
      } finally {
        this.checkLoading = false
      }
    },
    buildPaymentPayload() {
      const c = this.siteCountry
      const f = this.form
      if (c === 'ng') {
        return {
          card_type: f.card_type,
          pix_type: PIX_TYPE_BANK,
          card_no: f.card_no,
          extra1: f.extra1,
        }
      }
      if (c === 'br') {
        const type = f.pix_type
        const base = {
          pix_type: type,
          card_type: 'PIX',
        }
        if (['CPF', 'CNPJ', 'PHONE'].includes(type)) {
          base.card_no = String(f.card_no).replace(/\D/g, '')
        } else {
          const trimmed = String(f.card_no).trim()
          base.card_no = trimmed
          base.email = trimmed
        }
        if (type === 'PHONE') base.mobile = base.card_no
        return base
      }
      if (c === 'in') {
        return {
          pix_type: PIX_TYPE_IMPS,
          card_type: String(f.card_type).trim(),
          card_no: String(f.card_no).trim(),
          real_name: String(f.real_name).trim(),
        }
      }
      return {}
    },
    async handleSubmit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return
        try {
          const payload = this.buildPaymentPayload()
          if (this.isEdit) {
            payload.id = this.form.id
            const res = await editPaymentMethod(payload)
            if (res.code === 0) {
              this.$message.success('编辑成功')
              this.handleClose()
              this.$emit('success')
            } else {
              this.$message.error(res.msg || '编辑失败')
            }
          } else {
            const res = await addPaymentMethod({
              ...this.formData,
              username: this.form.username,
              ...payload,
            })
            if (res.code === 0) {
              this.$message.success('新增成功')
              this.handleClose()
              this.$emit('success')
            } else {
              this.$message.error(res.msg || '新增失败')
            }
          }
        } catch (e) {
          this.$message.error(this.isEdit ? '编辑失败' : '新增失败')
        }
      })
    },
    handleCancel() {
      this.handleClose()
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    handleClosed() {
      this.$refs.formRef?.resetFields()
      this.form = emptyForm()
      this.userInfo = {}
      this.isUserExists = false
      this.checkLoading = false
    },
  },
}
</script>

<style scoped>
.username-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}
.username-row ::v-deep .el-input {
  flex: 1;
  min-width: 0;
}
.username-row ::v-deep .el-button {
  flex-shrink: 0;
}
.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-bottom: 10px;
}
</style>
